import {makeAutoObservable, observable, runInAction} from 'mobx'
import {Debouncer} from 'yougile-util'
import {DataObjectId} from 'yougile-model'

interface SearchProgressValue {
  from?: number
  allHubs?: number
  processedHubs?: number
}

const progressDebouncer = new Debouncer(50)
const progressDebouncerLatest = new Debouncer(50)

export class SearchProgress {
  private map = new Map<DataObjectId, SearchProgressValue>()

  addProgress(companyId: DataObjectId): void {
    this.map.set(companyId, {})
    searchProgressUpdater.refresh()
  }

  endProgress(companyId: DataObjectId): void {
    searchProgressUpdater.refresh()

    setTimeout(() => {
      this.map.delete(companyId)
      searchProgressUpdater.refresh()
    }, 200)
  }

  setAllHubs(companyId: DataObjectId, value: number): void {
    const pr = this.getProgress(companyId) || {}

    pr.allHubs = value

    this.map.set(companyId, pr)
    searchProgressUpdater.refresh()
  }

  setProcessedHubs(companyId: DataObjectId, value: number): void {
    progressDebouncer.runButNotOften(() => {
      runInAction(() => {
        this.updateProgress(companyId, value)
      })
    })
    progressDebouncerLatest.runLatest(() => {
      runInAction(() => {
        this.updateProgress(companyId, value)
      })
    })
  }

  private updateProgress(companyId: DataObjectId, value: number): void {
    const pr = this.getProgress(companyId)
    if (!pr) {
      return
    }

    pr.processedHubs = value

    this.map.set(companyId, pr)
    searchProgressUpdater.refresh()
  }

  getProgress(companyId: DataObjectId): SearchProgressValue | undefined {
    return this.map.get(companyId)
  }

  getPercent(companyId: DataObjectId): string {
    const pr = this.getProgress(companyId)

    if (!pr) {
      return `${0}%`
    }

    const processedHubs = pr.processedHubs || 0
    const allHubs = pr.allHubs || 1

    const per = Math.round((processedHubs / allHubs) * 100)

    return `${per}%`
  }
}

export const searchProgress = new SearchProgress()

interface State {
  modified: number
}

export class SearchProgressUpdater {
  state: State

  constructor() {
    this.state = {modified: 0}
    makeAutoObservable(this, {state: observable.shallow}, {autoBind: true})
  }

  refresh(): void {
    this.state.modified = Date.now()
  }
}

export const searchProgressUpdater = new SearchProgressUpdater()
